import { useRef, useEffect, useState } from 'react';
import {
  Building2, Users, Ruler, TrendingUp, Rocket, Lightbulb, Zap, Wifi, Star, Globe
} from 'lucide-react';

const stats = [
  {
    icon: Users,
    value: '+1 milhão',
    label: 'clientes',
    description: 'Mais de um milhão de pessoas confiam nos serviços iGreen.',
  },
  {
    icon: Ruler,
    value: '+3.000 m²',
    label: 'sede própria',
    description: 'Estrutura própria em Uberlândia, Minas Gerais, com espaço completo.',
  },
  {
    icon: Building2,
    value: '+500',
    label: 'colaboradores',
    description: 'Equipe interna dedicada ao crescimento e atendimento.',
  },
];

type Logo = {
  src: string;
  alt: string;
  white?: boolean;
};

const timelineSteps = [
  {
    year: '2021',
    title: 'Origem',
    desc: 'Fundada por Thiago Alexander e Amanda Durante, em Minas Gerais. Nasce a iGreen. Uma visão, um propósito: "Enriquecer vidas e impactar o mundo com soluções acessíveis e inovadoras."',
    icon: Lightbulb,
    logos: [
      {
        src: 'https://apn.igreenenergy.com.br/assets/logos/igreen-logomarca-neon.svg',
        alt: 'iGreen',
        white: true,
      },
    ] as Logo[],
  },
  {
    year: '2023',
    title: 'Marco Histórico',
    desc: 'Aquisição de 50% da iGreen. Os gigantes do setor apostam na nossa missão. Bilhões de reais investidos em capacidade energética, nos tornando a empresa mais preparada para liderar a transição energética.',
    icon: TrendingUp,
    logos: [
      {
        src: 'https://static.readdy.ai/image/9ede618f03ccc7702e9977fd3ff98704/2ddfab51ae38e9c1dc8ca32be83101b4.png',
        alt: 'Vibra',
        white: true,
      },
      {
        src: 'https://static.readdy.ai/image/9ede618f03ccc7702e9977fd3ff98704/64b2eeb1f8b1abb3a9e0175d22390941.png',
        alt: 'Comerc Energia',
        white: true,
      },
    ] as Logo[],
  },
  {
    year: '2024',
    title: 'Expansão e Telecom',
    desc: '+ de 100 mil clientes em apenas 2 anos. Início da operação iGreen Telecom. Energia, tecnologia e conexão para todo o Brasil.',
    icon: Zap,
    logos: [] as Logo[],
    expansion: true,
  },
  {
    year: '2025',
    title: 'A maior do setor',
    desc: '+ de 450 mil clientes. A expansão se torna real e este é apenas o começo de uma grande jornada.',
    icon: Star,
    logos: [
      {
        src: 'https://static.readdy.ai/image/9ede618f03ccc7702e9977fd3ff98704/dc487e35c992dfccdf7f9a35a1cf9b2f.png',
        alt: 'Great Place To Work',
        white: false,
      },
    ] as Logo[],
  },
  {
    year: '2026',
    title: 'Dominação Global',
    desc: 'Reconhecida internacionalmente com destaque na Forbes, por meio da trajetória da CEO Amanda Durante. Mais de 1 milhão de clientes. Conexão Seguros, unindo energia, telecom e segurança em uma única plataforma de multisserviços.',
    icon: Globe,
    logos: [
      {
        src: 'https://static.readdy.ai/image/9ede618f03ccc7702e9977fd3ff98704/753bd35ca0ed79acfb7871b91a0aa149.png',
        alt: 'Forbes',
        white: true,
      },
    ] as Logo[],
  },
];

function StatCard({ stat, index }: { stat: typeof stats[0]; index: number }) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.15 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={`group text-center p-6 md:p-8 rounded-2xl bg-background-50/95 border border-background-200/70 transition-all duration-500 hover:-translate-y-1 hover:shadow-lg hover:border-primary-500/15 ${
        visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
      }`}
      style={{ transitionDelay: `${index * 100}ms` }}
    >
      <div className="w-16 h-16 rounded-xl bg-primary-100/80 ring-1 ring-primary-200/50 flex items-center justify-center text-primary-600 mx-auto mb-4 transition-transform duration-300 group-hover:scale-110">
        <stat.icon className="w-10 h-10" />
      </div>
      <div className="text-3xl md:text-4xl font-extrabold text-primary-600 mb-1">
        {stat.value}
      </div>
      <div className="text-sm font-semibold text-foreground-700 uppercase tracking-wide mb-2">
        {stat.label}
      </div>
      <p className="text-xs md:text-sm text-foreground-500 leading-relaxed">
        {stat.description}
      </p>
    </div>
  );
}

function ExpansionBadges() {
  const badges = [
    { icon: Users, label: '100K+' },
    { icon: Wifi, label: 'Telecom' },
    { icon: TrendingUp, label: 'Expansão' },
  ];

  return (
    <div className="mt-3 flex justify-center gap-4">
      {badges.map((b) => (
        <div key={b.label} className="flex flex-col items-center gap-1">
          <div className="w-10 h-10 rounded-full bg-primary-500/20 ring-1 ring-primary-400/30 flex items-center justify-center animate-pulse">
            <b.icon className="w-5 h-5 text-primary-400" />
          </div>
          <span className="text-[10px] font-semibold text-primary-400 uppercase tracking-wide">
            {b.label}
          </span>
        </div>
      ))}
    </div>
  );
}

function LogosRow({ logos }: { logos: Logo[] }) {
  if (logos.length === 0) return null;

  return (
    <div className="mt-3 flex flex-wrap justify-center gap-3 items-center">
      {logos.map((logo) => (
        <img
          key={logo.alt}
          src={logo.src}
          alt={logo.alt}
          className={
            logo.alt === 'Great Place To Work'
              ? 'h-auto w-[100px] max-w-full object-contain'
              : 'h-auto max-h-16 w-auto max-w-full object-contain'
          }
          style={logo.white ? { filter: 'brightness(0) invert(1)' } : undefined}
          loading="lazy"
        />
      ))}
    </div>
  );
}

export default function CompanySection() {
  const imgRef = useRef<HTMLDivElement>(null);
  const [imgVisible, setImgVisible] = useState(false);

  useEffect(() => {
    const el = imgRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setImgVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="empresa" className="relative overflow-hidden">
      {/* ====== Part 1: Stats with facade background ====== */}
      <div className="relative flex min-h-[100svh] w-full items-center overflow-hidden">
        <div ref={imgRef} className="absolute inset-0" aria-hidden="true">
          <img
            src="https://igreen-energy-lucas.lucaswantunes.chatgpt.site/assets/sede-fachada.jpg"
            alt=""
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-background-50/60" />
          <div className="absolute inset-0 bg-gradient-to-b from-background-50 via-transparent to-background-50" />
        </div>

        <div className="immersive-inner">
          <div className="mx-auto mb-8 max-w-3xl text-center md:mb-10">
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary-100 border border-primary-200 text-primary-700 text-sm font-semibold mb-5">
              <Building2 className="w-4 h-4" />
              Estrutura e credibilidade
            </span>
            <h2 className="font-heading text-2xl md:text-4xl lg:text-5xl font-extrabold text-foreground-950 tracking-tighter leading-[1.1]">
              Conheça a <span className="text-gradient">força</span> da <br className="hidden md:inline" />iGreen Energy
            </h2>
            <p className="mt-4 md:mt-5 text-base md:text-lg text-foreground-600 leading-relaxed">
              Estrutura própria, equipe interna, tecnologia e uma trajetória de crescimento que já alcançou mais de um milhão de clientes.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3 md:gap-5">
            {stats.map((stat, i) => (
              <StatCard key={stat.label} stat={stat} index={i} />
            ))}
          </div>

          {/* Sede visual */}
          <div className="relative z-10 mt-6 grid w-full grid-cols-1 gap-4 md:mt-8 md:grid-cols-2 md:gap-5">
              <div className="relative aspect-video overflow-hidden rounded-2xl shadow-xl">
                <img
                  src="https://static.readdy.ai/image/9ede618f03ccc7702e9977fd3ff98704/837f3c069255c522ec83e76bdfcf0082.png"
                  alt="Sede própria da iGreen no Brasil com mais de 3.000 m² e equipe interna de mais de 500 colaboradores"
                  className="h-full w-full rounded-2xl object-cover object-center"
                  loading="lazy"
                />
              </div>
              <div className="relative aspect-video overflow-hidden rounded-2xl shadow-xl">
                <img
                  src="https://static.readdy.ai/image/9ede618f03ccc7702e9977fd3ff98704/5bc1ea5b102c451c48bcd4d43df49a8a.png"
                  alt="Fachada iluminada da sede iGreen em Uberlândia, MG"
                  className="h-full w-full rounded-2xl object-cover object-center"
                  loading="lazy"
                />
              </div>
          </div>
        </div>
      </div>

      {/* ====== Part 2: Trajetória de crescimento — dark timeline ====== */}
      <div className="relative flex min-h-[100svh] w-full items-center overflow-hidden bg-background-950">
        {/* Neon glows */}
        <div className="absolute top-0 left-1/4 w-[600px] h-[400px] bg-radial-green rounded-full pointer-events-none opacity-20" />
        <div className="absolute bottom-0 right-1/4 w-[500px] h-[400px] bg-radial-green rounded-full pointer-events-none opacity-15" />

        <div className="immersive-inner">
          <div className="text-center max-w-2xl mx-auto mb-14 md:mb-20">
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary-500/10 border border-primary-500/20 text-primary-400 text-sm font-semibold mb-5">
              <Rocket className="w-4 h-4" />
              Trajetória de crescimento
            </span>
            <h2 className="font-heading text-2xl md:text-4xl lg:text-5xl font-extrabold text-white tracking-tighter leading-[1.1]">
              De <span className="text-primary-400">Uberlândia</span> para o <br className="hidden md:inline" />
              <span className="text-primary-400">mundo</span>
            </h2>
            <p className="mt-4 md:mt-5 text-base md:text-lg text-foreground-400 leading-relaxed">
              Uma história de visão, parcerias estratégicas e crescimento acelerado que posiciona a iGreen como referência nacional.
            </p>
          </div>

          {/* Desktop horizontal timeline */}
          <div className="hidden md:block relative">
            <div className="absolute left-0 right-0 top-[10px] h-0.5">
              <div className="w-full h-full bg-gradient-to-r from-transparent via-primary-400 to-transparent" />
            </div>
            <div className="grid grid-cols-5 gap-4 relative z-10">
              {timelineSteps.map((step) => (
                <div key={step.year} className="flex flex-col items-center text-center">
                  <div className="w-5 h-5 rounded-full bg-background-950 border-2 border-primary-400 shadow-[0_0_12px_2px] shadow-primary-400/40 mb-6" />
                  <div className="flex h-full w-full flex-col rounded-2xl bg-background-900/80 border border-background-800/50 p-5 hover:border-primary-500/30 transition-all duration-300 hover:-translate-y-1">
                    <div className="text-2xl font-black text-primary-400 mb-1">{step.year}</div>
                    <div className="flex items-center justify-center gap-1.5 mb-2">
                      <step.icon className="w-4 h-4 text-primary-400" />
                      <span className="text-sm font-bold text-white">{step.title}</span>
                    </div>
                    <p className="text-xs text-foreground-400 leading-relaxed mb-3">{step.desc}</p>
                    {'expansion' in step && step.expansion && <ExpansionBadges />}
                    <LogosRow logos={step.logos} />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Mobile vertical timeline */}
          <div className="md:hidden relative">
            <div className="absolute left-[7px] top-0 bottom-0 w-0.5">
              <div className="w-full h-full bg-gradient-to-b from-transparent via-primary-400 to-transparent" />
            </div>
            <div className="flex flex-col gap-8 relative z-10">
              {timelineSteps.map((step) => (
                <div key={step.year} className="flex items-start gap-4">
                  <div className="w-4 h-4 rounded-full bg-background-950 border-2 border-primary-400 shadow-[0_0_10px_2px] shadow-primary-400/40 shrink-0 mt-1" />
                  <div className="flex-1 rounded-2xl bg-background-900/80 border border-background-800/50 p-4 text-center">
                    <div className="text-xl font-black text-primary-400 mb-1">{step.year}</div>
                    <div className="mb-1 flex items-center justify-center gap-1.5">
                      <step.icon className="w-4 h-4 text-primary-400" />
                      <span className="text-sm font-bold text-white">{step.title}</span>
                    </div>
                    <p className="text-sm text-foreground-400 leading-relaxed mb-2">{step.desc}</p>
                    {'expansion' in step && step.expansion && <ExpansionBadges />}
                    <LogosRow logos={step.logos} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
